import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Trash2 } from 'lucide-react'

import {
  BlockPalette,
  authoringPointerSensorOptions,
} from '@/components/authoring'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { WeightAllocationBar } from '@/components/admin/evaluation-rubrics/weight-allocation-bar'
import { ladderLevelLabels } from '@/components/admin/evaluation-rubrics/rubric-read-helpers'
import { type RubricCriterion } from '@/hooks/use-evaluation-rubrics-admin'
import {
  newCriterionDraft,
  normalizeWeightsForKind,
  scaleTypeOf,
  slugifyCriterionKey,
  weightTotalForKind,
} from '@/lib/rubric-publish-payload'

type RubricInlineCriteriaListProps = {
  criteria: RubricCriterion[]
  kind: RubricCriterion['kind']
  onChange: (criteria: RubricCriterion[]) => void
  /** Focus areas: open L2 canvas for levels & skills. */
  onEditLevelsSkills?: (criterionKey: string) => void
  addLabel?: string
}

function InlineSortableRow({
  id,
  row,
  showWeight,
  onUpdate,
  onRemove,
  onEditLevelsSkills,
}: {
  id: string
  row: RubricCriterion
  showWeight: boolean
  onUpdate: (patch: Partial<RubricCriterion>) => void
  onRemove: () => void
  onEditLevelsSkills?: (criterionKey: string) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  }
  const levelCount = ladderLevelLabels(row).length
  const skillCount = row.component_skills?.length ?? 0
  const labelError = !row.label.trim()

  function handleLabelChange(label: string) {
    const patch: Partial<RubricCriterion> = { label }
    if (label.trim()) {
      const autoKey = slugifyCriterionKey(label, row.kind)
      const isAutoKey =
        !row.key
        || row.key.startsWith('dim_')
        || row.key.startsWith('focus_')
        || row.key.startsWith('req_')
        || row.key.startsWith('criterion_')
      if (isAutoKey) {
        patch.key = autoKey
      }
    }
    onUpdate(patch)
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`space-y-3 border-b px-3 py-3 last:border-b-0 ${isDragging ? 'opacity-80 shadow-md bg-background' : ''}`}
    >
      <div className="flex flex-wrap items-start gap-2">
        <button
          type="button"
          className="mt-2 cursor-grab text-muted-foreground hover:text-foreground shrink-0"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-start gap-2">
            <div className="min-w-0 flex-1 space-y-1">
              <Label className="sr-only">Name</Label>
              <Input
                value={row.label}
                onChange={(e) => handleLabelChange(e.target.value)}
                className={labelError ? 'border-destructive' : undefined}
                placeholder={
                  row.kind === 'competency'
                    ? 'e.g. Leadership'
                    : row.kind === 'requirement'
                      ? 'e.g. Open to hybrid work'
                      : 'e.g. Skills fit'
                }
              />
            </div>
            {showWeight ? (
              <div className="flex items-center gap-1 shrink-0 pt-0.5">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={5}
                  className="h-9 w-16 text-xs tabular-nums"
                  aria-label={`${row.label || row.key} weight percent`}
                  value={Math.round((row.weight ?? 0) * 100)}
                  onChange={(e) => {
                    const next = Number(e.target.value)
                    if (!Number.isFinite(next)) return
                    onUpdate({ weight: Math.max(0, Math.min(100, next)) / 100 })
                  }}
                />
                <span className="text-xs text-muted-foreground">%</span>
              </div>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              className="shrink-0 text-muted-foreground"
              aria-label={`Remove ${row.label || row.key || 'criterion'}`}
              onClick={onRemove}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-1">
            <Label className="sr-only">Definition</Label>
            <textarea
              value={row.definition ?? ''}
              onChange={(e) => onUpdate({ definition: e.target.value })}
              rows={2}
              className="flex min-h-[3.5rem] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              placeholder={
                row.kind === 'competency'
                  ? 'Describe observable behaviours or evidence.'
                  : 'How should this be assessed?'
              }
            />
          </div>
          {row.kind === 'competency' ? (
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="text-xs font-normal">
                {levelCount} {levelCount === 1 ? 'level' : 'levels'} · {skillCount}{' '}
                {skillCount === 1 ? 'skill' : 'skills'}
              </Badge>
              {onEditLevelsSkills ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onEditLevelsSkills(row.key)}
                >
                  Edit levels & skills
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </li>
  )
}

export function RubricInlineCriteriaList({
  criteria,
  kind,
  onChange,
  onEditLevelsSkills,
  addLabel,
}: RubricInlineCriteriaListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, authoringPointerSensorOptions),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const entries = criteria
    .map((row, i) => ({ row, i, id: `${row.key}::${i}` }))
    .filter(({ row }) => row.kind === kind)

  const showWeights =
    kind !== 'requirement' && entries.some(({ row }) => scaleTypeOf(row) !== 'boolean')
  const total = weightTotalForKind(criteria, kind)
  const segments = entries.map(({ row }) => ({
    key: row.key,
    label: row.label || row.key,
    weight: row.weight ?? 0,
  }))

  function updateRow(globalIndex: number, patch: Partial<RubricCriterion>) {
    onChange(criteria.map((row, i) => (i === globalIndex ? { ...row, ...patch } : row)))
  }

  function removeRow(globalIndex: number) {
    onChange(criteria.filter((_, i) => i !== globalIndex))
  }

  function addRow() {
    onChange([...criteria, newCriterionDraft(kind)])
  }

  function updateWeightForKey(key: string, weight: number) {
    onChange(
      criteria.map((row) => (row.key === key && row.kind === kind ? { ...row, weight } : row)),
    )
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const kindIndices = entries.map(({ i }) => i)
    const ids = entries.map((e) => e.id)
    const oldPos = ids.indexOf(String(active.id))
    const newPos = ids.indexOf(String(over.id))
    if (oldPos < 0 || newPos < 0) return
    const reorderedKindIndices = arrayMove(kindIndices, oldPos, newPos)
    const next = [...criteria]
    const kindRows = reorderedKindIndices.map((i) => criteria[i])
    let cursor = 0
    for (let i = 0; i < next.length; i += 1) {
      if (next[i].kind === kind) {
        next[i] = { ...kindRows[cursor], sort_order: cursor }
        cursor += 1
      }
    }
    onChange(next)
  }

  const resolvedAddLabel =
    addLabel
    ?? (kind === 'dimension'
      ? 'Add dimension'
      : kind === 'competency'
        ? 'Add focus area'
        : 'Add requirement')

  return (
    <div className="space-y-4">
      {showWeights ? (
        <WeightAllocationBar
          total={total}
          segments={segments}
          label={kind === 'dimension' ? 'Dimension weights' : 'Focus area weights'}
          onNormalize={() => onChange(normalizeWeightsForKind(criteria, kind))}
          onWeightChange={updateWeightForKey}
        />
      ) : null}

      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {kind === 'dimension'
            ? 'No match dimensions yet.'
            : kind === 'competency'
              ? 'No focus areas yet.'
              : 'No requirements yet.'}
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={entries.map((e) => e.id)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="rounded-md border text-sm">
              {entries.map(({ row, i, id }) => (
                <InlineSortableRow
                  key={id}
                  id={id}
                  row={row}
                  showWeight={showWeights}
                  onUpdate={(patch) => updateRow(i, patch)}
                  onRemove={() => removeRow(i)}
                  onEditLevelsSkills={onEditLevelsSkills}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      <BlockPalette
        title="Add criterion"
        className="@md:w-full"
        items={[
          {
            type: kind,
            label: resolvedAddLabel,
            description:
              kind === 'dimension'
                ? 'Weighted match dimension'
                : kind === 'competency'
                  ? 'Focus area with levels & skills'
                  : 'Pass/fail requirement',
          },
        ]}
        onAdd={() => addRow()}
      />
    </div>
  )
}
